import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchDirectory,
  fetchDirectoryPlace,
  resolveApaleoProperty,
} from "../services/reservationService";
import { useAppLanguage } from "../hooks/useAppLanguage";

const directoryTranslations = {
  de: {
    directory: "Hotel Directory",
    titleFallback: "Hotel Directory",
    loadingDirectory: "Hoteldirectory wird geladen...",
    loadingPlace: "Ort wird geladen...",
    unavailable: "Hoteldirectory ist gerade nicht verfügbar.",
    placeUnavailable: "Dieser Ort ist gerade nicht verfügbar.",
    noProperty: "Kein Property-Code für diesen Aufenthalt gefunden.",
    back: "Zurück",
    backHome: "Zurück zur Startseite",
    openMaps: "In Google Maps öffnen",
    infoFallback: "Information",
    placeFallback: "Ort",
    informationSoon: "Informationen sind bald verfügbar.",
    breakfastSoon: "Frühstücksinformationen sind bald verfügbar.",
    placesSoon: "Orte in der Nähe sind bald verfügbar.",
    directorySoon: "Hoteldirectory-Inhalte sind bald verfügbar.",
    tabLabel: "Directory Bereiche",
    sections: {
      info: "Infos",
      breakfast: "Frühstück",
      places: "Orte",
    },
  },
  en: {
    directory: "Hotel Directory",
    titleFallback: "Hotel Directory",
    loadingDirectory: "Loading hotel directory...",
    loadingPlace: "Loading place details...",
    unavailable: "Directory is not available right now.",
    placeUnavailable: "This place is not available right now.",
    noProperty: "No property code found for this stay.",
    back: "Back",
    backHome: "Back home",
    openMaps: "Open in Google Maps",
    infoFallback: "Information",
    placeFallback: "Place",
    informationSoon: "Information will be available here soon.",
    breakfastSoon: "Breakfast details will be available here soon.",
    placesSoon: "Nearby places will be available here soon.",
    directorySoon: "Hotel directory content will be available here soon.",
    tabLabel: "Directory sections",
    sections: {
      info: "Info",
      breakfast: "Breakfast",
      places: "Places",
    },
  },
};

const normalizePropertyCodeValue = (value) => String(value || "").trim().toUpperCase();

const resolveDirectoryPropertyCode = async (routePropertyCode) => {
  const propertyCode = normalizePropertyCodeValue(routePropertyCode);
  if (propertyCode) {
    localStorage.setItem("apaleo-property-id", propertyCode);
    return propertyCode;
  }

  const storedPropertyCode = normalizePropertyCodeValue(localStorage.getItem("apaleo-property-id"));
  if (storedPropertyCode) {
    return storedPropertyCode;
  }

  const hotelParam = localStorage.getItem("hotel-name");
  if (!hotelParam) {
    throw new Error("No property code found for this stay.");
  }

  const { propertyId } = await resolveApaleoProperty(hotelParam);
  const resolvedPropertyCode = normalizePropertyCodeValue(propertyId);
  localStorage.setItem("apaleo-property-id", resolvedPropertyCode);
  return resolvedPropertyCode;
};

const isPublished = (sectionWrap) => {
  if (!sectionWrap?.section) return false;
  return sectionWrap.section.is_published !== false;
};

const getAvailableSections = (directory) => {
  const sections = directory?.sections || {};
  const visibleSections = ["info", "breakfast"].filter((key) => isPublished(sections[key]));

  if (Array.isArray(directory?.places)) {
    visibleSections.push("places");
  }

  return visibleSections;
};

const getImageUrl = (item = {}) =>
  item.cover_image_url || item.image_url || item.image || item.url || "";

const getDescription = (item = {}) =>
  item.short_description || item.description || item.subtitle || "";

const getCardKey = (card, index) => card.id || card.slug || `${card.title || "card"}-${index}`;

const getPlaceSlug = (card = {}) => card.slug || card.metadata?.slug || "";

const getPublishedCards = (cards) => {
  if (!Array.isArray(cards)) return [];
  return cards
    .filter((card) => card?.is_published !== false)
    .sort((first, second) => (first.sort_order || 0) - (second.sort_order || 0));
};

const getPublishedPlaces = (directory) => getPublishedCards(directory?.places);

const richContentOptions = {
  ADD_TAGS: ["img"],
  ADD_ATTR: ["src", "alt", "title", "width", "height", "loading"],
};

const detailRichContentOptions = {
  ADD_TAGS: ["img"],
  ADD_ATTR: ["src", "alt", "title", "width", "height", "loading", "class"],
};

const RichContent = ({ html, allowClassName = false }) => {
  const sanitizedHtml = useMemo(() => DOMPurify.sanitize(
    html || "",
    allowClassName ? detailRichContentOptions : richContentOptions
  ), [allowClassName, html]);

  if (!sanitizedHtml) return null;

  return (
    <div
      className="directory-rich-content"
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

const EmptyState = ({ children }) => (
  <div className="directory-empty">{children}</div>
);

const DirectoryImage = ({ src, alt }) => {
  if (!src) return null;

  return <img className="directory-image" src={src} alt={alt || ""} loading="lazy" />;
};

const InfoSection = ({ data, t }) => {
  const cards = getPublishedCards(data?.cards);
  const section = data?.section || {};
  const hasSectionContent = Boolean(section.content_html);

  if (!cards.length && !hasSectionContent) {
    return <EmptyState>{t.informationSoon}</EmptyState>;
  }

  return (
    <div className="directory-stack">
      {hasSectionContent && (
        <section className="directory-content-block">
          <RichContent html={section.content_html} />
        </section>
      )}

      {cards.map((card, index) => (
        <article className="directory-info-card" key={getCardKey(card, index)}>
          <DirectoryImage src={getImageUrl(card)} alt={card.title} />
          <div>
            <h2>{card.title || t.infoFallback}</h2>
            {getDescription(card) && <p>{getDescription(card)}</p>}
            <RichContent html={card.content_html || card.detail_html} />
          </div>
        </article>
      ))}
    </div>
  );
};

const BreakfastSection = ({ data, t }) => {
  if (!data?.section) {
    return null;
  }

  const section = data.section;
  const hasContent = Boolean(section.content_html || section.subtitle || section.cover_image_url);

  if (!hasContent) {
    return <EmptyState>{t.breakfastSoon}</EmptyState>;
  }

  return (
    <section className="directory-breakfast">
      <DirectoryImage src={section.cover_image_url} alt={section.title} />
      {section.subtitle && <p className="directory-lead">{section.subtitle}</p>}
      <RichContent html={section.content_html} />
    </section>
  );
};

const PlacesSection = ({ places, onOpenPlace, t }) => {
  if (!places.length) {
    return <EmptyState>{t.placesSoon}</EmptyState>;
  }

  return (
    <div className="directory-stack">
      {places.map((card, index) => {
        const slug = getPlaceSlug(card);
        const cardBody = (
          <>
            <DirectoryImage src={getImageUrl(card)} alt={card.title} />
            <div className="directory-place-card-body">
              {card.distance_label && <span>{card.distance_label}</span>}
              <h2>{card.title || t.placeFallback}</h2>
              {getDescription(card) && <p>{getDescription(card)}</p>}
            </div>
          </>
        );

        if (!slug) {
          return (
            <article className="directory-place-card muted" key={getCardKey(card, index)}>
              {cardBody}
            </article>
          );
        }

        return (
          <button
            className="directory-place-card"
            key={getCardKey(card, index)}
            onClick={() => onOpenPlace(slug)}
            type="button"
          >
            {cardBody}
          </button>
        );
      })}
    </div>
  );
};

const PlaceDetail = ({ place, loading, error, onBack, t }) => {
  if (loading) {
    return <div className="directory-loader compact">{t.loadingPlace}</div>;
  }

  if (error) {
    return (
      <div className="directory-stack">
        <div className="directory-error">{error}</div>
        <button className="directory-button secondary" onClick={onBack} type="button">
          {t.back}
        </button>
      </div>
    );
  }

  if (!place) return null;

  const images = Array.isArray(place.images) ? place.images : [];

  return (
    <article className="directory-detail directory-place-detail" data-place-id={place.id || ""}>
      {place.custom_css ? <style data-directory-place-style>{place.custom_css}</style> : null}
      <button className="directory-back" onClick={onBack} type="button">
        {t.back}
      </button>
      <DirectoryImage src={place.cover_image_url} alt={place.title} />
      {place.distance_label && <p className="directory-kicker">{place.distance_label}</p>}
      <h1>{place.title}</h1>
      {place.short_description && <p className="directory-lead">{place.short_description}</p>}
      <RichContent html={place.detail_html} allowClassName />

      {images.length > 0 && (
        <div className="directory-gallery">
          {images.map((image, index) => {
            const src = typeof image === "string" ? image : getImageUrl(image);
            const alt = typeof image === "string" ? place.title : image.alt || place.title;
            return <DirectoryImage key={`${src}-${index}`} src={src} alt={alt} />;
          })}
        </div>
      )}

      {place.google_maps_url && (
        <a className="directory-button primary" href={place.google_maps_url} rel="noreferrer" target="_blank">
          {t.openMaps}
        </a>
      )}
    </article>
  );
};

const HotelDirectoryPage = () => {
  const { propertyCode: routePropertyCode, placeSlug } = useParams();
  const navigate = useNavigate();
  const [language] = useAppLanguage();
  const t = directoryTranslations[language] || directoryTranslations.de;
  const [propertyCode, setPropertyCode] = useState("");
  const [directory, setDirectory] = useState(null);
  const [activeSection, setActiveSection] = useState("info");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [placeLoading, setPlaceLoading] = useState(false);
  const [placeError, setPlaceError] = useState("");

  useEffect(() => {
    let isActive = true;

    const loadDirectory = async () => {
      try {
        setLoading(true);
        setError("");
        const code = await resolveDirectoryPropertyCode(routePropertyCode);
        const data = await fetchDirectory(code);

        if (!isActive) return;

        const responseCode = normalizePropertyCodeValue(data?.property?.propertyCode || code);
        const availableSections = getAvailableSections(data);

        setPropertyCode(responseCode);
        setDirectory(data);
        setActiveSection(placeSlug && availableSections.includes("places")
          ? "places"
          : availableSections[0] || "info");

        if (!routePropertyCode && responseCode) {
          navigate(`/hotel-directory/${encodeURIComponent(responseCode)}`, { replace: true });
        }
      } catch (err) {
        if (!isActive) return;
        const message = err.message === "No property code found for this stay."
          ? t.noProperty
          : err.response?.data?.error || err.response?.data?.detail || err.message || t.unavailable;
        setError(message);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadDirectory();

    return () => {
      isActive = false;
    };
  }, [navigate, placeSlug, routePropertyCode, t.noProperty, t.unavailable]);

  useEffect(() => {
    let isActive = true;

    if (!placeSlug) {
      setSelectedPlace(null);
      setPlaceError("");
      setPlaceLoading(false);
      return undefined;
    }

    if (!propertyCode) {
      return undefined;
    }

    const loadPlace = async () => {
      try {
        setActiveSection("places");
        setPlaceLoading(true);
        setPlaceError("");
        setSelectedPlace(null);
        const data = await fetchDirectoryPlace(propertyCode, placeSlug);

        if (!isActive) return;
        setSelectedPlace(data?.place || data);
      } catch (err) {
        if (!isActive) return;
        setPlaceError(err.response?.data?.error || err.response?.data?.detail || t.placeUnavailable);
      } finally {
        if (isActive) {
          setPlaceLoading(false);
        }
      }
    };

    loadPlace();

    return () => {
      isActive = false;
    };
  }, [placeSlug, propertyCode, t.placeUnavailable]);

  const availableSections = useMemo(() => getAvailableSections(directory), [directory]);
  const places = useMemo(() => getPublishedPlaces(directory), [directory]);
  const activeData = directory?.sections?.[activeSection];
  const activeTitle = activeData?.section?.title || t.sections[activeSection] || t.titleFallback;

  const openPlace = (slug) => {
    if (!propertyCode || !slug) return;

    navigate(`/hotel-directory/${encodeURIComponent(propertyCode)}/places/${encodeURIComponent(slug)}`);
  };

  const backToPlaces = () => {
    setActiveSection("places");
    navigate(`/hotel-directory/${encodeURIComponent(propertyCode)}`);
  };

  if (loading) {
    return (
      <div className="directory-app">
        <div className="directory-loader">{t.loadingDirectory}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="directory-app">
        <main className="directory-section">
          <p className="directory-kicker">{t.directory}</p>
          <h1>{t.titleFallback}</h1>
          <div className="directory-error">{error}</div>
          <a className="directory-button secondary" href="/home">{t.backHome}</a>
        </main>
      </div>
    );
  }

  if (placeSlug) {
    return (
      <div className="directory-app">
        <main className="directory-section">
          <PlaceDetail
            place={selectedPlace}
            loading={placeLoading}
            error={placeError}
            onBack={backToPlaces}
            t={t}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="directory-app">
      <main className="directory-section">
        <p className="directory-kicker">{directory?.property?.propertyCode || propertyCode}</p>
        <h1>{directory?.property?.name || t.titleFallback}</h1>
        {directory?.property?.address && <p className="directory-lead">{directory.property.address}</p>}

        {availableSections.length > 0 ? (
          <>
            <div className="directory-tabs" role="tablist" aria-label={t.tabLabel}>
              {availableSections.map((key) => (
                <button
                  className={activeSection === key ? "active" : ""}
                  key={key}
                  onClick={() => setActiveSection(key)}
                  type="button"
                >
                  {t.sections[key]}
                </button>
              ))}
            </div>

            <section className="directory-panel">
              {activeSection !== "places" && (
                <>
                  <p className="directory-kicker">{t.sections[activeSection]}</p>
                  <h2>{activeTitle}</h2>
                </>
              )}

              {activeSection === "info" && <InfoSection data={activeData} t={t} />}
              {activeSection === "breakfast" && isPublished(activeData) && <BreakfastSection data={activeData} t={t} />}
              {activeSection === "places" && <PlacesSection places={places} onOpenPlace={openPlace} t={t} />}
            </section>
          </>
        ) : (
          <EmptyState>{t.directorySoon}</EmptyState>
        )}
      </main>
    </div>
  );
};

export default HotelDirectoryPage;
